import { ReactNode } from 'react'
import { classNames } from '@/util/tailwindHelpers'
import { pluralize } from '@/util/stringHelpers'
import ControlsIsland from '@/components/ControlsIsland'
import CloudinaryImage from '@/components/CloudinaryImage'
import Button from '@/components/Button'
import { CircleIconButton } from '@/components/CircleIconLink'
import useBreakpoints from '@/hooks/useBreakpoints'
import { ModalButtonProps } from '@/components/Modals/ModalV2/types'

interface SelectionToolbarProps {
  onCancel: () => void
  onSelectAll: () => void
  value: {
    label: string
    component: ReactNode
    pluralValue?: number
  }
  primaryAction: ModalButtonProps
  selectedCount: number
  selectableCount: number
  className?: string
  bgImage?: {
    path: string
    id: string
  }
}

export const SelectionToolbar = ({
  className,
  primaryAction,
  value,
  onCancel,
  onSelectAll,
  selectedCount,
  selectableCount,
  bgImage
}: SelectionToolbarProps) => {
  const itemText = pluralize(value.label, value.pluralValue || selectedCount)
  const { isMedium } = useBreakpoints(['md'])

  const primaryButtonSize = isMedium ? 'md' : 'sm'

  const selectAllProps = {
    onSelectAll,
    selectableCount,
    selectedCount
  }

  return (
    <ControlsIsland
      showBottomGradient
      contentClassName={classNames(
        'p-[12px] pl-3 rounded-[49px] overflow-hidden relative bg-gray-800 border-1 border-transparent',
        className
      )}
      renderFooter={
        <div className={'md:hidden mt-[12px] w-full flex justify-center'}>
          <SelectAllButton {...selectAllProps} />
        </div>
      }
    >
      {bgImage && (
        <CloudinaryImage
          imageId={bgImage.id}
          layout="fill"
          objectFit="cover"
          alt="Convert Toolbar Background"
          path={bgImage.path}
        />
      )}

      <div className="relative flex justify-between gap-2 md:items-center w-full min-w-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center flex-shrink-0 gap-1 mr-2 md:pr-2 md:border-r border-gray-100/20">
            {value.component}
          </div>

          <SelectAllButton className="hidden md:flex" {...selectAllProps} />
        </div>

        <div className="flex gap-1 md:gap-2 min-w-0">
          <CancelButton
            onCancel={onCancel}
            className="max-md:order-1 flex-shrink-0"
          />

          <Button
            disabled={!selectedCount || primaryAction.disabled}
            className="w-full min-w-0 md:w-auto rounded-[42px] [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:block"
            theme={primaryAction.theme}
            size={primaryButtonSize}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label} {itemText}
          </Button>
        </div>
      </div>
    </ControlsIsland>
  )
}

interface SelectAllButtonProps {
  onSelectAll: () => void
  selectableCount: number
  selectedCount: number
  className?: string
}

const SelectAllButton = ({
  onSelectAll,
  selectableCount,
  selectedCount,
  className
}: SelectAllButtonProps) => {
  return (
    <Button
      className={classNames('utility', className)}
      disabled={selectedCount === selectableCount}
      theme="clean"
      onClick={onSelectAll}
    >
      Select All {selectableCount}
    </Button>
  )
}

interface CancelButtonProps {
  onCancel: () => void
  className?: string
}

const CancelButton = ({ onCancel, className }: CancelButtonProps) => {
  return (
    <>
      <Button
        className={classNames('hidden md:block utility ml-2', className)}
        theme="clean"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <CircleIconButton
        onClick={onCancel}
        name="menuClose"
        className={classNames(
          'md:hidden w-5 h-5 text-white flex-shrink-0',
          className
        )}
      />
    </>
  )
}
